import type {
  CharacterInfo,
  ModelInfo,
  RateLimitStatus,
  StructuredErrorResponse
} from '@gofranz/gpt-muse-common';
import {
  ActionIcon,
  Affix,
  Box,
  Center,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Tooltip,
  Transition,
  TypographyStylesProvider,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconArrowDown, IconCopy, IconRefresh, IconRobot, IconTrash, IconUser, IconWorld } from '@tabler/icons-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ChatMessage, ChatMessageAttachment, ChatMessageImage } from '../../lib/chat-db';
import { extractDomainsFromHtml, type DomainInfo } from '../../lib/chat-utils';
import { chatService } from '../../services/chatService';
import { streamChatCompletion } from '../../utils/streamingUtils';
import { ChatInputBar } from './ChatInputBar';
import './ChatStyles.css';
import { CodeBlock } from './CodeBlock';
import { ReasoningDisplay } from './ReasoningDisplay';
import { type ChatSettings, type ReasoningSettings, type WebSearchSettings } from './SettingsModal';

interface ChatInterfaceProps {
  messages: ChatMessage[];
  selectedModel: string;
  isAuthenticated: boolean;
  isLoading?: boolean;
  onSendMessage: (message: ChatMessage) => void;
  onReceiveMessage: (message: ChatMessage) => void;
  onMessageComplete?: (message: ChatMessage) => void; // Called after successful streaming
  onDeleteMessage?: (messageId: string) => void | Promise<void>; // Called when user wants to delete a message
  onRateLimitUpdate?: (rateLimits: RateLimitStatus | null) => void; // Called when rate limits are updated
  onTokenUsage?: (usage: { prompt_tokens: number; completion_tokens: number; total_tokens: number }) => void; // Called when token usage is received
  selectedCharacter?: string | null; // Character ID or 'custom'
  customPrompt?: string; // Custom prompt text when character is 'custom'
  rateLimitInfo?: RateLimitStatus | null; // Rate limit information for unauthenticated users
  onChatMemoryLimitChange?: (limit: number) => void; // Called when chat memory limit changes
  models?: ModelInfo[]; // Available models for settings
  onModelSelect?: (modelId: string) => void; // Called when user selects a model
  onCharacterSelect?: (characterId: string | null) => void; // Called when user selects a character
  isLoadingModels?: boolean; // Loading state for models
  inputPlaceholder?: string; // Dynamic placeholder text for the input
  characters?: CharacterInfo[]; // Available characters
}

export function ChatInterface({
  messages,
  selectedModel,
  isAuthenticated,
  isLoading = false,
  onSendMessage,
  onReceiveMessage,
  onMessageComplete,
  onDeleteMessage,
  onRateLimitUpdate,
  onTokenUsage,
  selectedCharacter,
  customPrompt,
  rateLimitInfo,
  onChatMemoryLimitChange,
  models = [],
  onModelSelect,
  onCharacterSelect,
  isLoadingModels = false,
  inputPlaceholder,
  characters = [],
}: ChatInterfaceProps) {
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [streamingReasoning, setStreamingReasoning] = useState('');
  const [streamingImages, setStreamingImages] = useState<ChatMessageImage[]>([]);
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
  const [reasoningSettings, setReasoningSettings] = useState<ReasoningSettings | null>(null);
  const [currentReasoningSettings, setCurrentReasoningSettings] = useState<ReasoningSettings | undefined>();
  const [currentChatSettings, setCurrentChatSettings] = useState<ChatSettings | undefined>();
  const [currentWebSearchSettings, setCurrentWebSearchSettings] = useState<WebSearchSettings | undefined>();
  const [messageDomains, setMessageDomains] = useState<Map<string, DomainInfo[]>>(new Map());
  const [isUserAtBottom, setIsUserAtBottom] = useState(true);
  const [justSentMessage, setJustSentMessage] = useState(false);
  const parentRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  
  const theme = useMantineTheme();
  const computedColorScheme = useComputedColorScheme('light');
  const isDark = computedColorScheme === 'dark';

  // Virtual scrolling setup
  const allMessages = useMemo(() => {
    const msgs = [...messages];
    if (streamingMessage || streamingReasoning || streamingImages.length > 0) {
      msgs.push({
        id: 'streaming',
        role: 'assistant' as const,
        content: streamingMessage,
        reasoning: streamingReasoning,
        images: streamingImages.length > 0 ? streamingImages : undefined,
        timestamp: new Date(),
        conversationId: '',
      });
    } else if (isStreaming && !streamingMessage && !streamingReasoning && streamingImages.length === 0) {
      msgs.push({
        id: 'thinking',
        role: 'assistant' as const,
        content: '',
        timestamp: new Date(),
        conversationId: '',
      });
    }
    return msgs;
  }, [messages, streamingMessage, streamingReasoning, streamingImages, isStreaming]);

  // Memoize virtualizer callbacks to prevent re-render loops
  const getScrollElement = useCallback(() => parentRef.current, []);
  const estimateSize = useCallback(() => 150, []);
  const getItemKey = useCallback((index: number) => allMessages[index]?.id || index, [allMessages]);

  const virtualizer = useVirtualizer({
    count: allMessages.length,
    getScrollElement,
    estimateSize,
    overscan: 5,
    getItemKey,
  });

  const scrollToBottom = useCallback(() => {
    if (parentRef.current && allMessages.length > 0) {
      virtualizer.scrollToIndex(allMessages.length - 1, { align: 'end' });
    }
  }, [allMessages.length, virtualizer]);

  const handleScroll = () => {
    if (!parentRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = parentRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
    setIsUserAtBottom(isAtBottom);
  };

  useEffect(() => {
    if (isUserAtBottom || justSentMessage) {
      // Use requestAnimationFrame to avoid flushSync warning from virtualizer
      requestAnimationFrame(() => {
        requestAnimationFrame(() => scrollToBottom());
      });
    }
    if (justSentMessage) {
      setJustSentMessage(false);
    }
  }, [allMessages.length, isUserAtBottom, justSentMessage, scrollToBottom]);

  // Auto-scroll when streaming content updates
  useEffect(() => {
    if (isUserAtBottom && (streamingMessage || streamingReasoning || streamingImages.length > 0)) {
      // Use requestAnimationFrame to avoid flushSync warning from virtualizer
      requestAnimationFrame(() => {
        requestAnimationFrame(() => scrollToBottom());
      });
    }
  }, [streamingMessage, streamingReasoning, streamingImages, isUserAtBottom, scrollToBottom]);

  // Load reasoning and web search settings when model changes
  useEffect(() => {
    const loadModelSettings = async () => {
      if (selectedModel) {
        try {
          const reasoningSettings = await chatService.getModelPreferences(selectedModel);
          const webSearchSettings = await chatService.getWebSearchPreferences(selectedModel);
          setReasoningSettings(reasoningSettings);
          setCurrentReasoningSettings(reasoningSettings || undefined);
          setCurrentWebSearchSettings(webSearchSettings || undefined);
        } catch (error) {
          console.error('Failed to load model settings:', error);
          setReasoningSettings(null);
          setCurrentReasoningSettings(undefined);
          setCurrentWebSearchSettings(undefined);
        }
      }
    };
    
    loadModelSettings();
  }, [selectedModel]);

  // Load chat settings from IndexedDB
  useEffect(() => {
    const loadChatSettings = async () => {
      try {
        const chatMemoryLimit = await chatService.getChatMemoryLimit();
        setCurrentChatSettings({ chatMemoryLimit });
      } catch (error) {
        console.error('Failed to load chat settings:', error);
        setCurrentChatSettings({ chatMemoryLimit: 20 }); // Default fallback
      }
    };

    loadChatSettings();
  }, []);

  const handleReasoningSettingsChange = async (modelId: string, settings: ReasoningSettings) => {
    try {
      await chatService.saveModelPreferences(modelId, settings);
      setCurrentReasoningSettings(settings);
      setReasoningSettings(settings);
      console.log('Reasoning settings saved for model:', modelId, settings);
    } catch (error) {
      console.error('Failed to save reasoning settings:', error);
    }
  };

  const handleChatSettingsChange = async (settings: ChatSettings) => {
    try {
      await chatService.saveChatMemoryLimit(settings.chatMemoryLimit);
      setCurrentChatSettings(settings);
      // Call parent callback for any additional handling
      onChatMemoryLimitChange?.(settings.chatMemoryLimit);
      console.log('Chat settings saved:', settings);
    } catch (error) {
      console.error('Failed to save chat settings:', error);
    }
  };

  const handleWebSearchSettingsChange = async (modelId: string, settings: WebSearchSettings) => {
    try {
      await chatService.saveWebSearchPreferences(modelId, settings);
      setCurrentWebSearchSettings(settings);
      console.log('Web search settings saved for model:', modelId, settings);
    } catch (error) {
      console.error('Failed to save web search settings:', error);
    }
  };


  const handleStopStreaming = useCallback(() => {
    // Abort the current request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Reset streaming states
    setIsStreaming(false);
    setStreamingMessage('');
    setStreamingReasoning('');
    setStreamingImages([]);
  }, []);

  const showErrorNotification = (error: string | { message?: string, code?: string } | StructuredErrorResponse) => {
    // Check if it's already a structured error response
    if (typeof error === 'object' && 'error_code' in error) {
      const structuredError = error as StructuredErrorResponse;
      notifications.show({
        title: structuredError.title,
        message: structuredError.message,
        color: 'red',
        autoClose: structuredError.is_temporary ? 8000 : 12000, // Longer display for permanent errors
      });
      return;
    }

    // Fallback for legacy error handling
    const errorString = typeof error === 'string' ? error : error.message || JSON.stringify(error);

    notifications.show({
      title: 'Chat Error',
      message: errorString,
      color: 'red',
      autoClose: 8000,
    });
  };


  const handleCopyMessage = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      // TODO: Add notification that copy was successful
      console.log('Message copied to clipboard');
    } catch (error) {
      console.error('Failed to copy message:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = content;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const handleDeleteMessage = (messageId: string) => {
    if (onDeleteMessage) {
      onDeleteMessage(messageId);
    }
  };

  const handleRetryMessage = async (assistantMessageId: string) => {
    // Find the assistant message and the user message that came before it
    const messageIndex = messages.findIndex(msg => msg.id === assistantMessageId);
    if (messageIndex === -1) return;

    // Find the last user message before this assistant message
    let userMessage: ChatMessage | undefined;
    for (let i = messageIndex - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        userMessage = messages[i];
        break;
      }
    }

    if (!userMessage) {
      console.error('No user message found before assistant message');
      return;
    }

    // Delete the assistant message (parent will handle state and database)
    if (onDeleteMessage) {
      await onDeleteMessage(assistantMessageId);
    }

    // Re-trigger the API call with the previous user message content
    await retryWithMessage(userMessage.content);
  };

  const retryWithMessage = async (messageContent: string) => {
    if (!selectedModel) return;

    setIsStreaming(true);
    setStreamingMessage('');
    setStreamingReasoning('');
    setStreamingImages([]);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const result = await streamChatCompletion({
      messages,
      selectedModel,
      messageContent,
      isAuthenticated,
      onRateLimitUpdate,
      onTokenUsage,
      onStreamingUpdate: (content, reasoning, images) => {
        setStreamingMessage(content);
        setStreamingReasoning(reasoning);
        setStreamingImages(images);
      },
      onError: showErrorNotification,
      abortSignal: abortController.signal,
    });

    setIsStreaming(false);
    setStreamingMessage('');
    setStreamingReasoning('');
    setStreamingImages([]);

    if (result.message) {
      onReceiveMessage(result.message);
      if (onMessageComplete) {
        onMessageComplete(result.message);
      }
    }

    abortControllerRef.current = null;
  };

  const handleSubmit = async (messageContent: string, attachment?: ChatMessageAttachment) => {
    if (!selectedModel || !messageContent.trim()) return;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      conversationId: '',
      role: 'user',
      content: messageContent.trim(),
      attachment,
      timestamp: new Date(),
    };

    onSendMessage(userMessage);
    setJustSentMessage(true);
    setIsStreaming(true);
    setStreamingMessage('');
    setStreamingReasoning('');
    setStreamingImages([]);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const isFirstMessage = messages.length === 0;

    const result = await streamChatCompletion({
      messages,
      selectedModel,
      messageContent: messageContent.trim(),
      attachment,
      isAuthenticated,
      selectedCharacter,
      customPrompt,
      isFirstMessage,
      onRateLimitUpdate,
      onTokenUsage,
      onStreamingUpdate: (content, reasoning, images) => {
        setStreamingMessage(content);
        setStreamingReasoning(reasoning);
        setStreamingImages(images);
      },
      onError: showErrorNotification,
      abortSignal: abortController.signal,
    });

    setIsStreaming(false);
    setStreamingMessage('');
    setStreamingReasoning('');
    setStreamingImages([]);

    if (result.message) {
      onReceiveMessage(result.message);
      onMessageComplete?.(result.message);
    }

    abortControllerRef.current = null;
  };

  // Extract domains from completed assistant messages  
  const extractDomainsForMessage = (messageId: string, htmlElement: HTMLElement) => {
    if (messageDomains.has(messageId)) {
      return; // Already extracted
    }
    
    try {
      const domains = extractDomainsFromHtml(htmlElement.innerHTML);
      if (domains.length > 0) {
        setMessageDomains(prev => new Map(prev.set(messageId, domains)));
      }
    } catch (error) {
      console.error('Error extracting domains:', error);
    }
  };

  const renderMessage = (message: ChatMessage) => {
    const handleMarkdownRef = (element: HTMLDivElement | null) => {
      if (element && message.role === 'assistant' && !messageDomains.has(message.id)) {
        // Extract domains from the rendered HTML
        extractDomainsForMessage(message.id, element);
      }
    };

    return (
    <Box 
      key={message.id} 
      mb="sm"
      onMouseEnter={() => setHoveredMessageId(message.id)}
      onMouseLeave={() => setHoveredMessageId(null)}
    >
      <Paper
          p={message.role === 'user' ? 'md' : ''}
        className="chat-message-container"
        style={{
          backgroundColor: message.role === 'user'
            ? 'var(--mantine-color-default-hover)'
            : message.isError
            ? 'var(--mantine-color-red-light)'
            : 'transparent',
          border: message.isError ? '1px solid var(--mantine-color-red-6)' : undefined,
          overflow: 'hidden',
          position: 'relative',
          // marginLeft: message.role === 'user' ? '10%' : '0',
          // marginRight: message.role === 'assistant' ? '10%' : '0',
        }}
      >
          <Group>
            {message.role === 'user' && (
              <ActionIcon
            size="sm"
            variant="light"
            color={message.role === 'user' ? theme.colors['brand-primary'][0] : 'gray'}
            style={{ flexShrink: 0 }}
          >
                <IconUser size={16} />
          </ActionIcon>
            )}

            {message.isError && (
              <ActionIcon
                size="sm"
                variant="light"
                color="red"
                style={{ flexShrink: 0 }}
              >
                <IconAlertCircle size={16} />
              </ActionIcon>
            )}

          <Box
            className="chat-message-content"
            style={{
              flex: '1 1 0',
              minWidth: 0,
              overflow: 'hidden',
              maxWidth: '100%'
            }}
          >
            {/* Show reasoning above content for assistant messages (unless excluded) */}
            {message.role === 'assistant' && message.reasoning && !reasoningSettings?.excludeReasoning && (
              <ReasoningDisplay reasoning={message.reasoning} />
            )}
            
            <Box className="chat-message-bubble" style={{ maxWidth: '100%' }}>
              <TypographyStylesProvider ref={handleMarkdownRef}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    pre: ({ children }) => {
                      // Extract code content from the pre block
                      const codeElement = Array.isArray(children) 
                        ? children.find((child: any) => child?.type === 'code') 
                        : children;
                      
                      const codeContent = codeElement?.props?.children || '';
                      const codeText = Array.isArray(codeContent) 
                        ? codeContent.join('') 
                        : String(codeContent);

                      // Extract language from className if available
                      const language = codeElement?.props?.className?.replace('language-', '') || '';

                      return (
                        <CodeBlock
                          code={codeText}
                          language={language}
                          className={codeElement?.props?.className}
                          isDark={isDark}
                        >
                          {codeContent}
                        </CodeBlock>
                      );
                    },
                    code: ({ children, className, ...props }) => {
                      // Handle inline code
                      const isInline = !className;
                      const codeText = Array.isArray(children) 
                        ? children.join('') 
                        : String(children);
                      
                      if (isInline) {
                        return (
                          <CodeBlock
                            code={codeText}
                            inline={true}
                            isDark={isDark}
                          >
                            {children}
                          </CodeBlock>
                        );
                      }

                      // Block code will be handled by pre component
                      return <code className={className} {...props}>{children}</code>;
                    },
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </TypographyStylesProvider>
            </Box>

            {/* Show user attachment if available */}
            {message.role === 'user' && message.attachment && (
              <Box mt="sm">
                {message.attachment.type === 'image' ? (
                  <img
                    src={`data:${message.attachment.mimeType};base64,${message.attachment.data}`}
                    alt="User uploaded image"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '400px',
                      height: 'auto',
                      borderRadius: theme.radius.sm,
                      border: `1px solid var(--mantine-color-default-border)`,
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      // Open image in new tab for full view
                      window.open(`data:${message.attachment!.mimeType};base64,${message.attachment!.data}`, '_blank');
                    }}
                  />
                ) : message.attachment.type === 'audio' ? (
                  <audio
                    controls
                    src={`data:${message.attachment.mimeType};base64,${message.attachment.data}`}
                    style={{
                      maxWidth: '100%',
                      borderRadius: theme.radius.sm,
                    }}
                  />
                ) : null}
              </Box>
            )}

            {/* Show streaming images if available (from assistant messages) */}
            {message.images && message.images.length > 0 && (
              <Stack gap="sm" mt="sm">
                {message.images.map((image, index) => (
                  <Box key={index}>
                    <img
                      src={image.url}
                      alt={`Generated image ${index + 1}`}
                      style={{
                        maxWidth: '100%',
                        height: 'auto',
                        borderRadius: theme.radius.sm,
                        border: `1px solid var(--mantine-color-default-border)`,
                        cursor: 'pointer', // Allow clicking to view larger
                      }}
                      onClick={() => {
                        // Open image in new tab for full view
                        window.open(image.url, '_blank');
                      }}
                    />
                  </Box>
                ))}
              </Stack>
              )}
          </Box>
        </Group>
      </Paper>
      
      {/* Domain favicons and action buttons below the message for assistant messages */}
      {message.role === 'assistant' && (
        <Group 
          justify="space-between"
          mt="xs"
        >
          {/* Domain favicons on the left */}
          <Group gap="xs">
            {messageDomains.get(message.id)?.map((domainInfo) => (
              <Tooltip
                key={domainInfo.url}
                label={
                  <div>
                    <Text size="sm" fw={500}>{domainInfo.domain}</Text>
                    <Text size="xs" c="dimmed">{domainInfo.url}</Text>
                  </div>
                }
                multiline
              >
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="gray"
                  component="a"
                  href={domainInfo.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    backgroundImage: `url(https://www.google.com/s2/favicons?domain=${domainInfo.domain}&sz=16)`,
                    backgroundSize: '12px 12px',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                  }}
                >
                  <IconWorld size={12} style={{ opacity: 0.3 }} />
                </ActionIcon>
              </Tooltip>
            ))}
          </Group>

          {/* Action buttons on the right (on hover) */}
          <Group
            gap="xs"
            style={{
              opacity: hoveredMessageId === message.id ? 1 : 0,
              transition: 'opacity 0.2s ease',
            }}
          >
              <Tooltip label="Copy message">
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="gray"
                  onClick={() => handleCopyMessage(message.content)}
                >
                  <IconCopy size={12} />
                </ActionIcon>
              </Tooltip>
              
              <Tooltip label="Retry message">
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="gray"
                  onClick={() => handleRetryMessage(message.id)}
                >
                  <IconRefresh size={12} />
                </ActionIcon>
              </Tooltip>
              
              <Tooltip label="Delete message">
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="red"
                  onClick={() => handleDeleteMessage(message.id)}
                >
                  <IconTrash size={12} />
                </ActionIcon>
              </Tooltip>
          </Group>
        </Group>
      )}
    </Box>
    );
  };

  const renderStreamingMessage = () => {
    if (!streamingMessage && !streamingReasoning && streamingImages.length === 0) return null;

    return (
      <Paper
        p=""
        mb="sm"
        className="chat-message-container"
        style={{
          backgroundColor: 'transparent',
          overflow: 'hidden',
          position: 'relative',
          // marginRight: '20%',
        }}
      >
        <Group>
          <Box
            className="chat-message-content"
            style={{
              flex: '1 1 0',
              minWidth: 0,
              overflow: 'hidden',
              maxWidth: '100%'
            }}
          >
            {/* Show streaming reasoning if available (unless excluded) */}
            {streamingReasoning && !reasoningSettings?.excludeReasoning && (
              <ReasoningDisplay reasoning={streamingReasoning} />
            )}
            
            {streamingMessage && (
              <Box className="chat-message-bubble" style={{ maxWidth: '100%' }}>
                <TypographyStylesProvider>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      pre: ({ children }) => {
                        // Extract code content from the pre block
                        const codeElement = Array.isArray(children) 
                          ? children.find((child: any) => child?.type === 'code') 
                          : children;
                        
                        const codeContent = codeElement?.props?.children || '';
                        const codeText = Array.isArray(codeContent) 
                          ? codeContent.join('') 
                          : String(codeContent);

                        // Extract language from className if available
                        const language = codeElement?.props?.className?.replace('language-', '') || '';

                        return (
                          <CodeBlock
                            code={codeText}
                            language={language}
                            className={codeElement?.props?.className}
                            isDark={isDark}
                          >
                            {codeContent}
                          </CodeBlock>
                        );
                      },
                      code: ({ children, className, ...props }) => {
                        // Handle inline code
                        const isInline = !className;
                        const codeText = Array.isArray(children) 
                          ? children.join('') 
                          : String(children);
                        
                        if (isInline) {
                          return (
                            <CodeBlock
                              code={codeText}
                              inline={true}
                              isDark={isDark}
                            >
                              {children}
                            </CodeBlock>
                          );
                        }

                        // Block code will be handled by pre component
                        return <code className={className} {...props}>{children}</code>;
                      },
                    }}
                  >
                    {streamingMessage}
                  </ReactMarkdown>
                </TypographyStylesProvider>
              </Box>
            )}
            
            {/* Show streaming images if available */}
            {streamingImages.length > 0 && (
              <Stack gap="sm" mt="sm">
                {streamingImages.map((image, index) => (
                  <Box key={index}>
                    <img 
                      src={image.url} 
                      alt={`Generated image ${index + 1}`} 
                      style={{
                        maxWidth: '100%',
                        height: 'auto',
                        borderRadius: theme.radius.sm,
                        border: `1px solid var(--mantine-color-default-border)`,
                      }} 
                    />
                  </Box>
                ))}
              </Stack>
            )}
            
            <Group gap="xs" mt="xs">
              <Loader size="xs" />
              <Text size="xs" c="dimmed">
                {streamingReasoning && !streamingMessage && !reasoningSettings?.excludeReasoning ? 'Thinking...' : 'Typing...'}
              </Text>
            </Group>
          </Box>
        </Group>
      </Paper>
    );
  };

  return (
    <Stack gap={0} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div
        ref={parentRef}
        onScroll={handleScroll}
        style={{
          flex: 1,
          width: '100%',
          overflow: 'auto',
          padding: '16px',
        }}
      >
        <Center>
          <Box
            style={{
              maxWidth: '900px',
              minWidth: 0,
              width: '100%',
            }}
          >
            {isLoading ? (
              <Center h="100%" style={{ minHeight: '200px' }}>
                <Stack align="center" gap="md">
                  <Loader size="lg" />
                  <Text size="sm" c="dimmed" ta="center">
                    Loading conversation...
                  </Text>
                </Stack>
              </Center>
            ) : allMessages.length === 0 ? (
              <Center h="100%" style={{ minHeight: '200px' }}>
                <Stack align="center" gap="md">
                  <IconRobot size={48} color="var(--mantine-color-gray-5)" />
                  <Text size="lg" c="dimmed" ta="center">
                    How can I help you today?
                  </Text>
                  <Text size="sm" c="dimmed" ta="center">
                    Start a conversation by typing a message below
                  </Text>
                </Stack>
              </Center>
            ) : (
              <div
                style={{
                  height: virtualizer.getTotalSize(),
                  width: '100%',
                  position: 'relative',
                }}
              >
                {virtualizer.getVirtualItems().map((virtualItem) => {
                  const message = allMessages[virtualItem.index];
                  return (
                    <div
                      key={virtualItem.key}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualItem.start}px)`,
                        padding: '8px 0', // Add padding to prevent overlap
                      }}
                      ref={virtualizer.measureElement}
                      data-index={virtualItem.index}
                    >
                      <div style={{ marginBottom: '16px' }}>
                        {message.id === 'streaming' ? (
                          renderStreamingMessage()
                        ) : message.id === 'thinking' ? (
                          <Paper
                            p="md"
                            mb="sm"
                            style={{
                              backgroundColor: 'var(--mantine-color-default-hover)',
                            }}
                          >
                            <Group gap="sm" align="center">
                              <ActionIcon size="sm" variant="light" color="gray">
                                <IconRobot size={16} />
                              </ActionIcon>
                              <Loader size="sm" />
                              <Text size="sm" c="dimmed">
                                Thinking...
                              </Text>
                            </Group>
                          </Paper>
                        ) : (
                          renderMessage(message)
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Box>
        </Center>
      </div>

      {/* Floating scroll-to-bottom button */}
      <Affix position={{ bottom: 100, right: 20 }}>
        <Transition transition="slide-up" mounted={!isUserAtBottom}>
          {(transitionStyles) => (
            <ActionIcon
              style={transitionStyles}
              size="lg"
              radius="xl"
              variant="filled"
              color="blue"
              onClick={() => {
                scrollToBottom();
                setIsUserAtBottom(true);
              }}
              aria-label="Scroll to bottom"
            >
              <IconArrowDown size={20} />
            </ActionIcon>
          )}
        </Transition>
      </Affix>

      <Box 
        px="md"
        py="sm"
        style={{
          borderTop: `1px solid var(--mantine-color-default-border)`,
          width: '100%',
          minWidth: 0,
          flexShrink: 0,
        }}
      >
        <ChatInputBar
          selectedModel={selectedModel}
          isAuthenticated={isAuthenticated}
          isLoading={isLoading}
          isStreaming={isStreaming}
          rateLimitInfo={rateLimitInfo}
          selectedCharacter={selectedCharacter}
          models={models}
          isLoadingModels={isLoadingModels}
          messages={messages}
          currentReasoningSettings={currentReasoningSettings}
          currentChatSettings={currentChatSettings}
          currentWebSearchSettings={currentWebSearchSettings}
          inputPlaceholder={inputPlaceholder}
          characters={characters}
          onSubmit={handleSubmit}
          onStopStreaming={handleStopStreaming}
          onModelSelect={onModelSelect}
          onCharacterSelect={onCharacterSelect}
          onReasoningSettingsChange={handleReasoningSettingsChange}
          onChatSettingsChange={handleChatSettingsChange}
          onWebSearchSettingsChange={handleWebSearchSettingsChange}
        />
      </Box>
    </Stack>
  );
}